<template>
  <div
    class="scenario-card p-2 mb-2"
    :class="{
      'is-draggable': isDraggable,
      'is-clickable': !isDraggable,
      'is-dragging': isDragging,
      'is-selected': isSelected,
      'is-puzzle': scenario.isPuzzle,
      'is-homework': scenario.isHomework,
    }"
    @click="$emit('select', scenario.id)"
    :draggable="isDraggable"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <div class="scenario-details">
      <p class="scenario-title title is-6 mb-2">
        {{ scenario.title || "Untitled Example" }}
        <span v-if="scenario.isPuzzle">🧩</span>
        <span v-if="scenario.isHomework">📚</span>
      </p>

      <div class="scenario-stats is-size-7 has-text-grey">
        <span v-if="moveCount">
          <b-icon icon="chess-pawn" size="is-small" />
          {{ moveCount }} moves:
          <span class="has-text-grey-light">{{ movesPreview }}</span>
        </span>
        <span v-if="hasComments">
          <b-icon icon="comment" size="is-small" />
        </span>
      </div>

      <div class="is-flex is-justify-content-space-between">
        <b-tag
          v-if="scenario.order"
          type="is-info is-light"
          size="is-small"
          rounded
        >
          #{{ scenario.order }}
        </b-tag>

        <PuzzleLevelBadge
          v-if="scenario.puzzleLevel"
          :level="scenario.puzzleLevel"
        />

        <TopicStatusBadge :status="scenario.status" />
      </div>
    </div>

    <BoardSnapshot :pgn="scenario.pgn" :board-size="90" />
  </div>
</template>

<script>
import TopicStatusBadge from "../atoms/TopicStatusBadge.vue";
import PuzzleLevelBadge from "../atoms/PuzzleLevelBadge.vue";
import BoardSnapshot from "@/components/training/explorer/molecules/BoardSnapshot.vue";
import { PgnParser } from "vue-chess-shared/pgn/PgnParser";

export default {
  name: "ScenarioCard",

  components: {
    TopicStatusBadge,
    PuzzleLevelBadge,
    BoardSnapshot,
  },

  props: {
    scenario: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isDraggable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDragging: false,
    };
  },

  methods: {
    handleDragStart(event) {
      if (!this.isDraggable) return;

      this.isDragging = true;
      event.dataTransfer.effectAllowed = "copy";
      event.dataTransfer.setData(
        "text/plain",
        JSON.stringify({
          id: this.scenario.id,
          title: this.scenario.title,
        })
      );
    },

    handleDragEnd() {
      this.isDragging = false;
    },
  },

  computed: {
    moveCount() {
      if (!this.scenario.pgn) return 0;
      return PgnParser.getMoveCount(this.scenario.pgn);
    },

    movesPreview() {
      if (!this.scenario.pgn) return "";
      if (this.moveCount === 0) return "";
      if (this.scenario.isPuzzle) return "";

      try {
        const { moves } = PgnParser.splitHeadersAndMoves(this.scenario.pgn);
        // Return first 10 characters
        return moves.substring(0, 10) + (moves.length > 10 ? "..." : "");
      } catch (error) {
        return "";
      }
    },

    hasComments() {
      return this.scenario.pgn?.includes("{") || false;
    },
  },
};
</script>

<style scoped>
.scenario-card {
  transition: all 0.2s ease;
  border: 1px solid #dbdbdb;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.scenario-card:hover {
  transform: translateY(-2px);
  border-color: #34369e;
}

.scenario-card.is-draggable {
  cursor: grab;
}

.scenario-card.is-clickable {
  cursor: pointer;
}

.scenario-card.is-selected {
  border-color: #3273dc;
  background-color: #f5f9ff;
}

.scenario-card.is-puzzle {
  border-left: 3px solid #ff9f43;
}

.scenario-card.is-homework {
  border-left: 3px solid #2f3542;
}

.scenario-card.is-dragging {
  opacity: 0.5;
  cursor: grabbing;
}

.scenario-details {
  flex: 1;
}

.scenario-title {
  min-height: 2.4em;
  max-height: 2.4em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  margin-bottom: 0.5rem !important;
  text-align: left;
}

.scenario-stats {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.scenario-stats span {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
